import { create } from 'zustand';
import moment from 'moment';
import { getWeekId, getPreviousWeekId, getFirstWeekIdOfYear } from 'src/utils/dateUtils';
import { microTrendsServerProxy } from 'src/serverProxy/microTrendsServerProxy';
import queryString from 'qs';
import { addPersistentQueryParams } from 'src/utils/browser';
import {
  DorisGroupByFieldNameEnum,
  DorisMetricTypeEnum
} from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';

// Simple helper functions to avoid circular dependencies with stringFormatting
const capitalize = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const titleCase = (str: string): string => {
  if (!str) return str;
  return str
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

interface MicroTrendFilterModalState {
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
}

interface MicroTrendsFilterState {
  selectedCategoryIds: number[];
  selectedSubcategoryIds: number[];
  setSelectedCategoryIds: (categoryIds: number[]) => void;
  setSelectedSubcategoryIds: (subcategoryIds: number[]) => void;
  clear: () => void;
}

export interface MicroTrendTimePeriod {
  id: 'ytd' | '13w' | '26w' | '52w';
  label: string;
  startWeek: number;
  endWeek: number;
}

interface MicroTrendsTimePeriodState {
  timePeriod: MicroTrendTimePeriod;
  availableEndWeek: number | null;
  setTimePeriod: (periodId: MicroTrendTimePeriod['id']) => void;
  setAvailableEndWeek: (endWeek: number) => void;
}

const calculateTimePeriod = (
  periodId: MicroTrendTimePeriod['id'],
  availableEndWeek: number | null
): MicroTrendTimePeriod => {
  // Use available end week from API response, or fall back to last week
  const currentWeekId = availableEndWeek ?? getWeekId(moment().subtract(1, 'week').toDate());

  switch (periodId) {
    case 'ytd': {
      const currentYear = Math.floor(currentWeekId / 100);
      const startWeek = getFirstWeekIdOfYear(currentYear);
      return {
        id: 'ytd',
        label: 'YTD',
        startWeek,
        endWeek: currentWeekId
      };
    }
    case '13w': {
      const startWeek = getPreviousWeekId(currentWeekId, 12);
      return {
        id: '13w',
        label: 'Last 13 weeks',
        startWeek,
        endWeek: currentWeekId
      };
    }
    case '26w': {
      const startWeek = getPreviousWeekId(currentWeekId, 25);
      return {
        id: '26w',
        label: 'Last 26 weeks',
        startWeek,
        endWeek: currentWeekId
      };
    }
    case '52w': {
      const startWeek = getPreviousWeekId(currentWeekId, 51);
      return {
        id: '52w',
        label: 'Last 52 weeks',
        startWeek,
        endWeek: currentWeekId
      };
    }
    default:
      return calculateTimePeriod('52w', availableEndWeek);
  }
};

export const useMicroTrendFilterModalStore = create<MicroTrendFilterModalState>((set) => ({
  isOpen: false,
  openModal: () => set({ isOpen: true }),
  closeModal: () => set({ isOpen: false })
}));

export const useMicroTrendsFilterStore = create<MicroTrendsFilterState>((set) => {
  return {
    selectedCategoryIds: [],
    selectedSubcategoryIds: [],
    setSelectedCategoryIds: (categoryIds: number[]) => set({ selectedCategoryIds: categoryIds }),
    setSelectedSubcategoryIds: (subcategoryIds: number[]) => set({ selectedSubcategoryIds: subcategoryIds }),
    clear: () => set({ selectedCategoryIds: [], selectedSubcategoryIds: [] })
  };
});

export const useMicroTrendsTimePeriodStore = create<MicroTrendsTimePeriodState>((set, get) => ({
  timePeriod: calculateTimePeriod('52w', null),
  availableEndWeek: null,
  setTimePeriod: (periodId: MicroTrendTimePeriod['id']) => {
    const { availableEndWeek } = get();
    const newTimePeriod = calculateTimePeriod(periodId, availableEndWeek);
    set({ timePeriod: newTimePeriod });
  },
  setAvailableEndWeek: (endWeek: number) => {
    const { timePeriod } = get();
    const newTimePeriod = calculateTimePeriod(timePeriod.id, endWeek);
    set({ availableEndWeek: endWeek, timePeriod: newTimePeriod });
  }
}));

interface SaveAsSegmentParams {
  mainEntity: any;
  retailer: any;
  mainTimePeriod: any;
  getEntityDisplayName: (id: string | number, fieldName: string) => string;
}

interface MicroTrendSaveAsSegmentState {
  isLoading: boolean;
  saveAsSegment: (params: SaveAsSegmentParams) => Promise<string | null>;
  reset: () => void;
}

export const useMicroTrendSaveAsSegmentStore = create<MicroTrendSaveAsSegmentState>((set) => ({
  isLoading: false,
  reset: () => set({ isLoading: false }),
  saveAsSegment: async ({ mainEntity, retailer, mainTimePeriod, getEntityDisplayName }: SaveAsSegmentParams) => {
    if (mainEntity.type !== 'microtrend') return null;

    try {
      set({ isLoading: true });

      const trendId = String(mainEntity.id);
      const requestBody = {
        retailerId: Number(retailer.id),
        startWeekId: mainTimePeriod.startWeek,
        endWeekId: mainTimePeriod.endWeek
      };

      const trendFilters = await microTrendsServerProxy.getTrendFilters(trendId, requestBody);

      if (!trendFilters.found) {
        set({ isLoading: false });
        return null;
      }

      const { subcategoryIds, brandIds, productTagFilters } = trendFilters;
      const isBrandTrend = mainEntity.trendType === 'brand';

      const segmentParams: any = {
        entityType: 'segment',
        action: 'create'
      };

      let segmentName = '';

      const subcategoryId = subcategoryIds && subcategoryIds.length > 0 ? subcategoryIds[0] : null;
      const subcategoryName = subcategoryId ? getEntityDisplayName(subcategoryId, 'subcategoryId') : '';

      if (isBrandTrend && mainEntity.brandName) {
        segmentName = subcategoryName ? `${mainEntity.brandName} - ${subcategoryName}` : mainEntity.brandName;
      } else if (productTagFilters && productTagFilters.length > 0) {
        const tagFilter = productTagFilters[0];
        const tagName = titleCase(tagFilter.label);
        const tagValue = capitalize(tagFilter.values[0]);
        segmentName = subcategoryName ? `${tagName} (${tagValue}) - ${subcategoryName}` : `${tagName} (${tagValue})`;
      }

      if (segmentName) {
        segmentParams.dn = segmentName;
      }

      if (subcategoryIds && subcategoryIds.length > 0) {
        segmentParams.sc = subcategoryId ? [{ i: subcategoryId }] : [];
      }

      if (brandIds && brandIds.length > 0) {
        segmentParams.b = brandIds.map((id) => ({ i: id }));
      }

      const isProductTagTrend = !isBrandTrend && productTagFilters && productTagFilters.length > 0;

      if (isProductTagTrend) {
        const productRequest = {
          metricType: DorisMetricTypeEnum.ProductsWithSalesData,
          fieldName: DorisGroupByFieldNameEnum.Product,
          retailerId: Number(retailer.id),
          categoryIds: [],
          subcategoryIds: [],
          brandIds: [],
          segmentIds: [],
          businessunitids: [],
          resultCount: 1000,
          trendIds: [trendId],
          startWeekId: mainTimePeriod.startWeek,
          endWeekId: mainTimePeriod.endWeek,
          sortby: DorisMetricTypeEnum.RetailSales
        };

        const productSkusResponse = await microTrendsServerProxy.getProductSkus(productRequest);

        if (productSkusResponse.records && productSkusResponse.records.length > 0) {
          const skus = productSkusResponse.records.slice(0, 500).map((record) => ({ i: record.fieldKey }));
          segmentParams.kp = skus;
        }
      }

      const searchParams = addPersistentQueryParams(retailer, mainTimePeriod);
      const segmentQueryString = queryString.stringify(segmentParams, { arrayLimit: 550 } as any);

      const url = `/search?${searchParams}&${segmentQueryString}`;
      return url;
    } catch (error) {
      console.error('Error saving as segment:', error);
      set({ isLoading: false });
      return null;
    }
  }
}));
